import { useState, useEffect } from "react";
import Card from "@/components/Card";
import Button from "@/components/Button";
import {
  CreditCard,
  Building2,
  Users,
  Check,
  Loader2,
  ArrowRight,
  Crown,
  Sparkles,
  ExternalLink,
  Calendar,
  Receipt,
  XCircle,
  AlertTriangle,
  CalendarDays,
  UserCheck,
  Clock,
  Zap,
  X,
  Phone,
  MessageCircle,
} from "lucide-react";
import apiClient from "@/lib/api-client";
import { getTrialDaysLeft } from "@/hooks/useSubscription";
import { usePermissions } from "@/hooks/usePermissions";
import { useLocation } from "@/contexts/LocationContext";
import { useGlobalToast } from "../contexts/ToastContext";

interface Plan {
  id: string;
  name: string;
  description: string;
  price: number;
  interval: string;
  features: string[];
  maxLocations: number;
  maxUsers: number;
  maxReservations: number | null; // null = unlimited (total, not per month)
  maxCustomers: number | null; // null = unlimited
  hasTableMaps: boolean;
  hasWhatsApp: boolean;
  hasVoiceAi: boolean;
  hasEmailCampaigns: boolean;
  trialDays: number;
  stripeProductId?: string;
  stripePriceId?: string;
}

interface SubscriptionData {
  organizationId: string;
  organizationName: string;
  subscriptionStatus: string;
  trialEndsAt: string | null;
  isInTrial: boolean;
  currentPlan: Plan;
  usage: {
    users: number;
    locations: number;
    totalReservations: number;
    totalCustomers: number;
  };
  stripeSubscriptionId?: string;
  subscriptionCurrentPeriodEnd?: string;
  subscriptionCancelAtPeriodEnd?: boolean;
}

interface StripeConfig {
  configured: boolean;
  hasCustomer: boolean;
  hasSubscription: boolean;
}

interface AddOnData {
  id: string;
  code: string;
  name: string;
  description: string | null;
  price: number;
  interval: string;
  isEligible: boolean;
  requiredPlanFeature: string | null;
  subscription: {
    id: string;
    status: string;
    currentPeriodEnd: string | null;
    cancelAtPeriodEnd: boolean;
  } | null;
}

export default function Subscription() {
  const { can } = usePermissions();
  const { refreshLocations, currentBookingMode } = useLocation();
  const isAppointmentsMode = currentBookingMode === "appointments";
  const locationLabel = isAppointmentsMode ? "centro" : "restaurante";
  const locationLabelPlural = isAppointmentsMode ? "centros" : "restaurantes";
  const reservationLabelPlural = isAppointmentsMode ? "citas" : "reservas";
  const { showToast } = useGlobalToast();
  const [subscription, setSubscription] = useState<SubscriptionData | null>(
    null,
  );
  // Importe real de Stripe: los clientes antiguos conservan su precio
  const [billedAmount, setBilledAmount] = useState<number | null>(null);
  const [availablePlans, setAvailablePlans] = useState<Plan[]>([]);
  const [stripeConfig, setStripeConfig] = useState<StripeConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [changingPlan, setChangingPlan] = useState<string | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [isOpeningPortal, setIsOpeningPortal] = useState(false);
  const [isStartingCheckout, setIsStartingCheckout] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isReactivating, setIsReactivating] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [addOns, setAddOns] = useState<AddOnData[]>([]);
  const [subscribingAddOn, setSubscribingAddOn] = useState<string | null>(null);
  const [cancellingAddOn, setCancellingAddOn] = useState<string | null>(null);
  const [reactivatingAddOn, setReactivatingAddOn] = useState<string | null>(
    null,
  );
  const [addOnToCancel, setAddOnToCancel] = useState<AddOnData | null>(null);

  const isAdmin = can("facturacion:gestionar");

  useEffect(() => {
    fetchData();
    checkStripeConfig();
    fetchAddOns();

    // Check for add-on checkout success
    const params = new URLSearchParams(window.location.search);
    if (params.get("addon_success") === "true") {
      showToast("¡Servicio adicional contratado exitosamente!", "success");
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const [subscriptionRes, plansRes] = await Promise.all([
        apiClient.get("/organizations/me/subscription"),
        apiClient.get("/plans"),
      ]);
      setSubscription(subscriptionRes.data.data);
      setAvailablePlans(plansRes.data.data);
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error && "response" in err
          ? (err as { response?: { data?: { message?: string } } }).response
              ?.data?.message
          : "Error al cargar los datos";
      showToast(errorMessage || "Error al cargar los datos", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const checkStripeConfig = async () => {
    try {
      const response = await apiClient.get("/stripe/config");
      setStripeConfig(response.data.data);
      // Not gated on hasSubscription: /stripe/config is a public route and
      // always reports false. Isolated so a failure here can't hide Stripe.
      apiClient
        .get("/stripe/subscription")
        .then((sub) =>
          setBilledAmount(sub.data.data?.stripe?.billedAmount ?? null),
        )
        .catch(() => setBilledAmount(null));
    } catch {
      setStripeConfig({
        configured: false,
        hasCustomer: false,
        hasSubscription: false,
      });
    }
  };

  const fetchAddOns = async () => {
    try {
      const response = await apiClient.get("/add-ons");
      setAddOns(response.data.data);
    } catch {
      // Non-critical, add-ons section simply won't render
    }
  };

  const handleSubscribeAddOn = async (addOnId: string) => {
    try {
      setSubscribingAddOn(addOnId);
      const response = await apiClient.post(`/add-ons/${addOnId}/subscribe`);
      // El backend devuelve la URL de Stripe Checkout en data.url
      // (se mantiene checkoutUrl como fallback por compatibilidad).
      const checkoutUrl =
        response.data.data?.url || response.data.data?.checkoutUrl;
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      } else {
        showToast("¡Servicio adicional activado!", "success");
        await fetchAddOns();
      }
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error && "response" in err
          ? (err as { response?: { data?: { message?: string } } }).response
              ?.data?.message
          : "Error al contratar el servicio";
      showToast(errorMessage || "Error al contratar el servicio", "error");
    } finally {
      setSubscribingAddOn(null);
    }
  };

  const handleCancelAddOn = async (addOnId: string) => {
    try {
      setCancellingAddOn(addOnId);
      await apiClient.post(`/add-ons/${addOnId}/cancel`);
      showToast(
        "El servicio se cancelará al final del período actual",
        "success",
      );
      await fetchAddOns();
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error && "response" in err
          ? (err as { response?: { data?: { message?: string } } }).response
              ?.data?.message
          : "Error al cancelar el servicio";
      showToast(errorMessage || "Error al cancelar el servicio", "error");
    } finally {
      setCancellingAddOn(null);
      setAddOnToCancel(null);
    }
  };

  const handleReactivateAddOn = async (addOnId: string) => {
    try {
      setReactivatingAddOn(addOnId);
      await apiClient.post(`/add-ons/${addOnId}/reactivate`);
      showToast("¡Servicio adicional reactivado!", "success");
      await fetchAddOns();
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error && "response" in err
          ? (err as { response?: { data?: { message?: string } } }).response
              ?.data?.message
          : "Error al reactivar el servicio";
      showToast(errorMessage || "Error al reactivar el servicio", "error");
    } finally {
      setReactivatingAddOn(null);
    }
  };

  const handlePlanSelect = (plan: Plan) => {
    if (!isAdmin) return;
    if (plan.id === subscription?.currentPlan.id) return;
    setSelectedPlan(plan);
    setShowConfirmModal(true);
  };

  const handleConfirmChange = async () => {
    if (!selectedPlan) return;

    if (
      selectedPlan.price > 0 &&
      stripeConfig?.configured &&
      selectedPlan.stripePriceId
    ) {
      try {
        setIsStartingCheckout(true);
        const response = await apiClient.post("/stripe/checkout", {
          planId: selectedPlan.id,
        });
        window.location.href = response.data.data.url;
      } catch (err: unknown) {
        const errorMessage =
          err instanceof Error && "response" in err
            ? (err as { response?: { data?: { message?: string } } }).response
                ?.data?.message
            : "Error al iniciar el checkout";
        showToast(errorMessage || "Error al iniciar el checkout", "error");
        setIsStartingCheckout(false);
      }
      return;
    }

    try {
      setChangingPlan(selectedPlan.id);
      const response = await apiClient.put("/organizations/me/plan", {
        planId: selectedPlan.id,
      });
      showToast(response.data.message, "success");
      setShowConfirmModal(false);
      setSelectedPlan(null);
      await Promise.all([fetchData(), refreshLocations()]);
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error && "response" in err
          ? (err as { response?: { data?: { message?: string } } }).response
              ?.data?.message
          : "Error al cambiar el plan";
      showToast(errorMessage || "Error al cambiar el plan", "error");
    } finally {
      setChangingPlan(null);
    }
  };

  const handleOpenBillingPortal = async () => {
    try {
      setIsOpeningPortal(true);
      const response = await apiClient.get("/stripe/portal");
      window.open(response.data.data.url, "_blank");
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error && "response" in err
          ? (err as { response?: { data?: { message?: string } } }).response
              ?.data?.message
          : "Error al abrir el portal";
      showToast(errorMessage || "Error al abrir el portal", "error");
    } finally {
      setIsOpeningPortal(false);
    }
  };

  const handleCancelSubscription = async () => {
    try {
      setIsCancelling(true);
      await apiClient.post("/stripe/cancel");
      showToast(
        "Tu suscripción se cancelará al final del período actual",
        "success",
      );
      setShowCancelModal(false);
      await fetchData();
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error && "response" in err
          ? (err as { response?: { data?: { message?: string } } }).response
              ?.data?.message
          : "Error al cancelar la suscripción";
      showToast(errorMessage || "Error al cancelar la suscripción", "error");
    } finally {
      setIsCancelling(false);
    }
  };

  const handleReactivateSubscription = async () => {
    try {
      setIsReactivating(true);
      await apiClient.post("/stripe/reactivate-scheduled");
      showToast("¡Tu suscripción ha sido reactivada!", "success");
      await fetchData();
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error && "response" in err
          ? (err as { response?: { data?: { message?: string } } }).response
              ?.data?.message
          : "Error al reactivar la suscripción";
      showToast(errorMessage || "Error al reactivar la suscripción", "error");
    } finally {
      setIsReactivating(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      active: "bg-green-100 text-green-800",
      trialing: "bg-blue-100 text-blue-800",
      past_due: "bg-yellow-100 text-yellow-800",
      cancelled: "bg-red-100 text-red-800",
      suspended: "bg-gray-100 text-gray-800",
    };
    const labels: Record<string, string> = {
      active: "Activa",
      trialing: "Periodo de prueba",
      past_due: "Pago pendiente",
      cancelled: "Cancelada",
      suspended: "Suspendida",
    };
    return (
      <span
        className={`px-3 py-1 rounded-full text-sm font-medium ${styles[status] || styles.active}`}
      >
        {labels[status] || status}
      </span>
    );
  };

  const canChangeToPlan = (plan: Plan): { can: boolean; reason?: string } => {
    if (!subscription) return { can: false, reason: "Cargando..." };
    if (plan.id === subscription.currentPlan.id)
      return { can: false, reason: "Plan actual" };
    if (subscription.usage.users > plan.maxUsers) {
      return {
        can: false,
        reason: `Tienes ${subscription.usage.users} usuarios (máx. ${plan.maxUsers})`,
      };
    }
    if (subscription.usage.locations > plan.maxLocations) {
      return {
        can: false,
        reason: `Tienes ${subscription.usage.locations} ${locationLabelPlural} (máx. ${plan.maxLocations})`,
      };
    }
    return { can: true };
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("es-ES", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900">
            Mi Suscripción
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Gestiona tu plan de suscripción y facturación
          </p>
        </div>
        {stripeConfig?.configured && isAdmin && (
          <Button
            variant="outline"
            onClick={handleOpenBillingPortal}
            disabled={isOpeningPortal}
          >
            {isOpeningPortal ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Receipt className="h-4 w-4 mr-2" />
            )}
            Portal de facturación
            <ExternalLink className="h-4 w-4 ml-2" />
          </Button>
        )}
      </div>

      {subscription?.subscriptionStatus === "past_due" && (
        <div className="p-4 bg-yellow-50 border border-yellow-200 text-yellow-800 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-medium">Pago pendiente</h3>
              <p className="text-sm mt-1">
                Tu último pago no se ha podido procesar. Por favor, actualiza tu
                método de pago.
              </p>
              {stripeConfig?.configured && (
                <Button
                  variant="primary"
                  size="sm"
                  className="mt-3"
                  onClick={handleOpenBillingPortal}
                  disabled={isOpeningPortal}
                >
                  {isOpeningPortal ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <CreditCard className="h-4 w-4 mr-2" />
                  )}
                  Actualizar método de pago
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Trial Banner */}
      {subscription?.isInTrial && subscription.trialEndsAt && (
        <div className="p-4 bg-gradient-to-r from-primary-50 to-blue-50 border border-primary-200 text-primary-800 rounded-lg">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-primary-100 rounded-full">
              <Zap className="h-5 w-5 text-primary-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-primary-900">
                ¡Estás en periodo de prueba!
              </h3>
              <p className="text-sm mt-1">
                Tienes acceso a <strong>todas las funciones premium</strong>{" "}
                hasta el <strong>{formatDate(subscription.trialEndsAt)}</strong>
                .
                {(() => {
                  const daysLeft = getTrialDaysLeft(subscription.trialEndsAt);
                  return daysLeft > 0 ? ` (${daysLeft} días restantes)` : "";
                })()}
              </p>
              <p className="text-xs mt-2 text-primary-600">
                Después del trial, tu cuenta pasará al plan Free con límites de
                20 {reservationLabelPlural} en total y 10 clientes.
              </p>
            </div>
          </div>
        </div>
      )}

      {subscription?.subscriptionCancelAtPeriodEnd && (
        <div className="p-4 bg-orange-50 border border-orange-200 text-orange-800 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-medium">Cancelación programada</h3>
              <p className="text-sm mt-1">
                Tu suscripción se cancelará el{" "}
                <strong>
                  {subscription.subscriptionCurrentPeriodEnd
                    ? formatDate(subscription.subscriptionCurrentPeriodEnd)
                    : "fin del período"}
                </strong>
                .
              </p>
              {isAdmin && (
                <Button
                  variant="primary"
                  size="sm"
                  className="mt-3"
                  onClick={handleReactivateSubscription}
                  disabled={isReactivating}
                >
                  {isReactivating ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Check className="h-4 w-4 mr-2" />
                  )}
                  Mantener mi suscripción
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {subscription && (
        <Card>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-primary-100 rounded-xl">
                <Crown className="h-8 w-8 text-primary-600" />
              </div>
              <div>
                <div className="flex items-center gap-3 mb-1 flex-wrap">
                  <h2 className="text-xl font-bold text-gray-900">
                    {subscription.currentPlan.name}
                  </h2>
                  {getStatusBadge(subscription.subscriptionStatus)}
                </div>
                <p className="text-gray-600">
                  {subscription.currentPlan.description || "Tu plan actual"}
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
                  <div className="flex items-center gap-2 text-sm">
                    <Users className="h-4 w-4 text-primary-500" />
                    <span className="text-gray-600">
                      {subscription.usage.users}/
                      {subscription.currentPlan.maxUsers} usuarios
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Building2 className="h-4 w-4 text-primary-500" />
                    <span className="text-gray-600">
                      {subscription.usage.locations}/
                      {subscription.currentPlan.maxLocations}{" "}
                      {locationLabelPlural}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <CalendarDays className="h-4 w-4 text-primary-500" />
                    <span className="text-gray-600">
                      {subscription.usage.totalReservations}/
                      {subscription.isInTrial ||
                      subscription.currentPlan.maxReservations === null
                        ? "∞"
                        : subscription.currentPlan.maxReservations}{" "}
                      {reservationLabelPlural}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <UserCheck className="h-4 w-4 text-primary-500" />
                    <span className="text-gray-600">
                      {subscription.usage.totalCustomers}/
                      {subscription.isInTrial ||
                      subscription.currentPlan.maxCustomers === null
                        ? "∞"
                        : subscription.currentPlan.maxCustomers}{" "}
                      clientes
                    </span>
                  </div>
                </div>
                {subscription.subscriptionCurrentPeriodEnd &&
                  !subscription.subscriptionCancelAtPeriodEnd && (
                    <div className="flex items-center gap-1 mt-3 text-sm text-gray-500">
                      <Calendar className="h-4 w-4" />
                      Próxima factura:{" "}
                      {formatDate(subscription.subscriptionCurrentPeriodEnd)}
                    </div>
                  )}
              </div>
            </div>
            <div className="text-right">
              {billedAmount !== null &&
                billedAmount < subscription.currentPlan.price && (
                  <div className="text-lg text-gray-400 line-through">
                    {subscription.currentPlan.price}€
                  </div>
                )}
              <div className="text-3xl font-bold text-gray-900">
                {billedAmount ?? subscription.currentPlan.price}€
                <span className="text-base font-normal text-gray-500">
                  /
                  {subscription.currentPlan.interval === "monthly"
                    ? "mes"
                    : "año"}
                </span>
              </div>
              {billedAmount !== null &&
                billedAmount < subscription.currentPlan.price && (
                  <div className="text-sm font-medium text-green-700 mt-1">
                    Precio especial mantenido
                  </div>
                )}
              {isAdmin &&
                subscription.currentPlan.price > 0 &&
                stripeConfig?.hasSubscription &&
                !subscription.subscriptionCancelAtPeriodEnd && (
                  <button
                    onClick={() => setShowCancelModal(true)}
                    className="text-sm text-gray-500 hover:text-red-600 mt-2 transition-colors"
                  >
                    Cancelar suscripción
                  </button>
                )}
            </div>
          </div>
        </Card>
      )}

      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-2">
          Planes Disponibles
        </h2>
        <p className="text-gray-500 text-sm">
          {isAdmin
            ? "Selecciona un plan para cambiar tu suscripción"
            : "Solo quien tenga permiso de facturación puede cambiar el plan"}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {availablePlans.map((plan) => {
          const isCurrentPlan = plan.id === subscription?.currentPlan.id;
          const { can: canChange, reason } = canChangeToPlan(plan);
          const isChanging = changingPlan === plan.id;
          const isPaidPlan = plan.price > 0;

          return (
            <div
              key={plan.id}
              className={`relative bg-white rounded-xl border-2 p-6 transition-all ${
                isCurrentPlan
                  ? "border-primary-500 ring-2 ring-primary-100"
                  : canChange && isAdmin
                    ? "border-gray-200 hover:border-primary-300 hover:shadow-lg cursor-pointer"
                    : "border-gray-200 opacity-75"
              }`}
              onClick={() => canChange && isAdmin && handlePlanSelect(plan)}
            >
              {isCurrentPlan && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-primary-600 text-white text-sm font-medium rounded-full">
                    <Sparkles className="h-3 w-3" />
                    Plan Actual
                  </span>
                </div>
              )}

              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-primary-100 rounded-xl mb-3">
                  <CreditCard className="h-6 w-6 text-primary-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">{plan.name}</h3>
                <p className="text-sm text-gray-500 mt-1">
                  {isAppointmentsMode
                    ? (plan.description || "").replace(
                        /restaurante[s]?/gi,
                        locationLabel,
                      ) || "Plan de suscripción"
                    : plan.description || "Plan de suscripción"}
                </p>
              </div>

              <div className="text-center mb-6">
                <span className="text-4xl font-bold text-gray-900">
                  {isCurrentPlan && billedAmount !== null ? billedAmount : plan.price}€
                </span>
                <span className="text-gray-500">
                  /{plan.interval === "monthly" ? "mes" : "año"}
                </span>
              </div>

              <div className="space-y-3 mb-6">
                {/* Limits */}
                <div className="flex items-center text-sm text-gray-600">
                  <Building2 className="h-4 w-4 mr-2 text-primary-500" />
                  <span>
                    Hasta {plan.maxLocations}{" "}
                    {plan.maxLocations !== 1
                      ? locationLabelPlural
                      : locationLabel}
                  </span>
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <Users className="h-4 w-4 mr-2 text-primary-500" />
                  <span>
                    Hasta {plan.maxUsers} usuario
                    {plan.maxUsers !== 1 ? "s" : ""}
                  </span>
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <CalendarDays className="h-4 w-4 mr-2 text-primary-500" />
                  <span>
                    {plan.maxReservations == null
                      ? `${reservationLabelPlural.charAt(0).toUpperCase() + reservationLabelPlural.slice(1)} ilimitadas`
                      : `${plan.maxReservations} ${reservationLabelPlural} en total`}
                  </span>
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <UserCheck className="h-4 w-4 mr-2 text-primary-500" />
                  <span>
                    {plan.maxCustomers == null
                      ? "Clientes ilimitados"
                      : `Hasta ${plan.maxCustomers} clientes`}
                  </span>
                </div>

                {/* Divider */}
                <div className="border-t border-gray-100 my-2"></div>

                {/* Features */}
                <div
                  className={`flex items-center text-sm ${plan.hasTableMaps ? "text-gray-600" : "text-gray-400"}`}
                >
                  {plan.hasTableMaps ? (
                    <Check className="h-4 w-4 mr-2 text-green-500" />
                  ) : (
                    <X className="h-4 w-4 mr-2 text-gray-300" />
                  )}
                  <span>
                    {isAppointmentsMode
                      ? "Especialistas y agenda avanzada"
                      : "Mapa visual de mesas"}
                  </span>
                </div>
                <div
                  className={`flex items-center text-sm ${plan.hasWhatsApp ? "text-gray-600" : "text-gray-400"}`}
                >
                  {plan.hasWhatsApp ? (
                    <Check className="h-4 w-4 mr-2 text-green-500" />
                  ) : (
                    <X className="h-4 w-4 mr-2 text-gray-300" />
                  )}
                  <span>Notificaciones WhatsApp</span>
                </div>
                <div
                  className={`flex items-center text-sm ${plan.hasEmailCampaigns ? "text-gray-600" : "text-gray-400"}`}
                >
                  {plan.hasEmailCampaigns ? (
                    <Check className="h-4 w-4 mr-2 text-green-500" />
                  ) : (
                    <X className="h-4 w-4 mr-2 text-gray-300" />
                  )}
                  <span>Campañas de email con IA</span>
                </div>
                <div
                  className={`flex items-start text-sm ${plan.hasVoiceAi ? "text-gray-600" : "text-gray-400"}`}
                >
                  {plan.hasVoiceAi ? (
                    <Check className="h-4 w-4 mr-2 mt-0.5 text-green-500 flex-shrink-0" />
                  ) : (
                    <X className="h-4 w-4 mr-2 mt-0.5 text-gray-300 flex-shrink-0" />
                  )}
                  <span>
                    Centralita IA por voz
                    <span className="block text-xs text-gray-400">
                      Add-on opcional, se contrata y factura aparte
                    </span>
                  </span>
                </div>
                {/* WhatsApp IA comparte gate con la Centralita (hasVoiceAi =
                    "el plan permite los add-ons de IA", mínimo Pro). */}
                <div
                  className={`flex items-start text-sm ${plan.hasVoiceAi ? "text-gray-600" : "text-gray-400"}`}
                >
                  {plan.hasVoiceAi ? (
                    <Check className="h-4 w-4 mr-2 mt-0.5 text-green-500 flex-shrink-0" />
                  ) : (
                    <X className="h-4 w-4 mr-2 mt-0.5 text-gray-300 flex-shrink-0" />
                  )}
                  <span>
                    WhatsApp IA (Carmen responde el chat)
                    <span className="block text-xs text-gray-400">
                      Add-on opcional, se contrata y factura aparte
                    </span>
                  </span>
                </div>

                {/* Trial info */}
                {plan.trialDays > 0 && (
                  <div className="flex items-center text-sm text-primary-600">
                    <Clock className="h-4 w-4 mr-2" />
                    <span>{plan.trialDays} días de prueba gratis</span>
                  </div>
                )}

                {/* Custom features */}
                {Array.isArray(plan.features) &&
                  plan.features.slice(0, 3).map((feature, index) => (
                    <div
                      key={index}
                      className="flex items-center text-sm text-gray-600"
                    >
                      <Check className="h-4 w-4 mr-2 text-green-500" />
                      <span>{feature}</span>
                    </div>
                  ))}
              </div>

              {isCurrentPlan ? (
                <Button variant="outline" className="w-full" disabled>
                  <Check className="h-4 w-4 mr-2" />
                  Plan Actual
                </Button>
              ) : isAdmin ? (
                <Button
                  variant={canChange ? "primary" : "outline"}
                  className="w-full"
                  disabled={!canChange || isChanging || isStartingCheckout}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (canChange) handlePlanSelect(plan);
                  }}
                >
                  {isChanging ||
                  (isStartingCheckout && selectedPlan?.id === plan.id) ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {isPaidPlan ? "Iniciando..." : "Cambiando..."}
                    </>
                  ) : canChange ? (
                    <>
                      {isPaidPlan && stripeConfig?.configured
                        ? "Suscribirse"
                        : "Cambiar a este plan"}
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </>
                  ) : (
                    reason
                  )}
                </Button>
              ) : (
                <Button variant="outline" className="w-full" disabled>
                  Solo quien tenga permiso de facturación puede cambiar
                </Button>
              )}
            </div>
          );
        })}
      </div>

      {/* Add-ons Section */}
      {addOns.length > 0 && (
        <>
          <div className="mt-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">
              Servicios Adicionales
            </h2>
            <p className="text-gray-500 text-sm">
              Servicios opcionales que se contratan y facturan{" "}
              <strong>aparte de tu plan</strong>, solo si los activas. Tu plan
              determina cuáles puedes contratar.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {addOns.map((addOn) => {
              const sub = addOn.subscription;
              // isActiveOrPending: subscription exists AND status is "active" (includes cancelAtPeriodEnd)
              const isActiveOrPending = !!sub && sub.status === "active";
              const isCancelScheduled =
                isActiveOrPending && sub!.cancelAtPeriodEnd;
              const isActive = isActiveOrPending && !sub!.cancelAtPeriodEnd;
              // canContract: no subscription, or subscription is cancelled/past_due/etc.
              const canContract = !isActiveOrPending;
              const isSubscribing = subscribingAddOn === addOn.id;
              const isCancellingThis = cancellingAddOn === addOn.id;
              const isReactivatingThis = reactivatingAddOn === addOn.id;

              return (
                <div
                  key={addOn.id}
                  className={`relative bg-white rounded-xl border-2 p-6 transition-all ${
                    isActiveOrPending
                      ? "border-primary-500 ring-2 ring-primary-100"
                      : "border-gray-200"
                  }`}
                >
                  {isActive && !isCancelScheduled && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-600 text-white text-sm font-medium rounded-full">
                        <Check className="h-3 w-3" />
                        Activo
                      </span>
                    </div>
                  )}
                  {isCancelScheduled && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className="inline-flex items-center gap-1 px-3 py-1 bg-orange-500 text-white text-sm font-medium rounded-full">
                        <AlertTriangle className="h-3 w-3" />
                        Cancelación programada
                      </span>
                    </div>
                  )}

                  <div className="text-center mb-4 mt-2">
                    <div className="inline-flex items-center justify-center w-12 h-12 bg-purple-100 rounded-xl mb-3">
                      {addOn.code === "whatsapp_ai" || addOn.code === "social_ai" ? (
                        <MessageCircle className="h-6 w-6 text-purple-600" />
                      ) : (
                        <Phone className="h-6 w-6 text-purple-600" />
                      )}
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">
                      {addOn.name}
                    </h3>
                    {addOn.description && (
                      <p className="text-sm text-gray-500 mt-1">
                        {addOn.description}
                      </p>
                    )}
                  </div>

                  <div className="text-center mb-6">
                    <span className="text-4xl font-bold text-gray-900">
                      {addOn.price}€
                    </span>
                    <span className="text-gray-500">
                      /{addOn.interval === "monthly" ? "mes" : "año"}
                    </span>
                  </div>

                  {!addOn.isEligible && canContract && (
                    <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <p className="text-sm text-yellow-800 flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                        Tu plan actual no incluye este servicio. Mejora tu plan
                        para poder contratarlo.
                      </p>
                    </div>
                  )}

                  {isActive &&
                    addOn.subscription?.currentPeriodEnd &&
                    !isCancelScheduled && (
                      <div className="mb-4 flex items-center justify-center gap-1 text-sm text-gray-500">
                        <Calendar className="h-4 w-4" />
                        Próxima factura:{" "}
                        {formatDate(addOn.subscription.currentPeriodEnd)}
                      </div>
                    )}

                  {isCancelScheduled &&
                    addOn.subscription?.currentPeriodEnd && (
                      <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                        <p className="text-sm text-orange-800">
                          Activo hasta el{" "}
                          <strong>
                            {formatDate(addOn.subscription.currentPeriodEnd)}
                          </strong>
                        </p>
                      </div>
                    )}

                  {/* Action buttons */}
                  {isAdmin && (
                    <>
                      {canContract && (
                        <Button
                          variant={addOn.isEligible ? "primary" : "outline"}
                          className="w-full"
                          disabled={!addOn.isEligible || isSubscribing}
                          onClick={() => handleSubscribeAddOn(addOn.id)}
                        >
                          {isSubscribing ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Contratando...
                            </>
                          ) : addOn.isEligible ? (
                            <>
                              Contratar servicio
                              <ArrowRight className="h-4 w-4 ml-2" />
                            </>
                          ) : (
                            "No disponible en tu plan"
                          )}
                        </Button>
                      )}
                      {isActive && !isCancelScheduled && (
                        <button
                          className="w-full text-sm text-gray-500 hover:text-red-600 mt-2 transition-colors py-2"
                          onClick={() => setAddOnToCancel(addOn)}
                          disabled={isCancellingThis}
                        >
                          {isCancellingThis ? (
                            <span className="flex items-center justify-center gap-2">
                              <Loader2 className="h-4 w-4 animate-spin" />
                              Cancelando...
                            </span>
                          ) : (
                            "Cancelar servicio"
                          )}
                        </button>
                      )}
                      {isCancelScheduled && (
                        <Button
                          variant="primary"
                          className="w-full"
                          disabled={isReactivatingThis}
                          onClick={() => handleReactivateAddOn(addOn.id)}
                        >
                          {isReactivatingThis ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Reactivando...
                            </>
                          ) : (
                            <>
                              <Check className="h-4 w-4 mr-2" />
                              Mantener servicio
                            </>
                          )}
                        </Button>
                      )}
                    </>
                  )}
                  {!isAdmin && isActive && (
                    <Button variant="outline" className="w-full" disabled>
                      <Check className="h-4 w-4 mr-2" />
                      Activo
                    </Button>
                  )}
                  {!isAdmin && isCancelScheduled && (
                    <Button variant="outline" className="w-full" disabled>
                      Activo hasta fin de período
                    </Button>
                  )}
                  {!isAdmin && canContract && (
                    <Button variant="outline" className="w-full" disabled>
                      Solo quien tenga permiso de facturación puede contratar
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {showConfirmModal && selectedPlan && subscription && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              {selectedPlan.price > 0 && stripeConfig?.configured
                ? "Confirmar suscripción"
                : "Confirmar cambio de plan"}
            </h3>
            <p className="text-gray-600 mb-4">
              {selectedPlan.price > 0 && stripeConfig?.configured ? (
                <>
                  Serás redirigido a la página de pago seguro para suscribirte
                  al plan <strong>{selectedPlan.name}</strong>.
                </>
              ) : (
                <>
                  ¿Estás seguro de cambiar del plan{" "}
                  <strong>{subscription.currentPlan.name}</strong> al plan{" "}
                  <strong>{selectedPlan.name}</strong>?
                </>
              )}
            </p>
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <div className="flex justify-between items-center mb-2">
                <span className="text-gray-600">Plan actual:</span>
                <span className="font-medium">
                  {subscription.currentPlan.name} -{" "}
                  {billedAmount ?? subscription.currentPlan.price}€/
                  {subscription.currentPlan.interval === "monthly"
                    ? "mes"
                    : "año"}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Nuevo plan:</span>
                <span className="font-medium text-primary-600">
                  {selectedPlan.name} - {selectedPlan.price}€/
                  {selectedPlan.interval === "monthly" ? "mes" : "año"}
                </span>
              </div>
            </div>
            {selectedPlan.price > 0 && stripeConfig?.configured && (
              <p className="text-sm text-gray-500 mb-4 flex items-center gap-2">
                <CreditCard className="h-4 w-4" />
                Pago seguro procesado por Stripe
              </p>
            )}
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setShowConfirmModal(false);
                  setSelectedPlan(null);
                }}
                disabled={changingPlan !== null || isStartingCheckout}
              >
                Cancelar
              </Button>
              <Button
                variant="primary"
                className="flex-1"
                onClick={handleConfirmChange}
                disabled={changingPlan !== null || isStartingCheckout}
              >
                {changingPlan || isStartingCheckout ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {selectedPlan.price > 0 ? "Iniciando..." : "Cambiando..."}
                  </>
                ) : selectedPlan.price > 0 && stripeConfig?.configured ? (
                  <>
                    Ir al pago
                    <ExternalLink className="h-4 w-4 ml-2" />
                  </>
                ) : (
                  "Confirmar cambio"
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {showCancelModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-full">
                <XCircle className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900">
                Cancelar suscripción
              </h3>
            </div>
            <p className="text-gray-600 mb-4">
              ¿Estás seguro de cancelar tu suscripción? Tu cuenta seguirá activa
              hasta el final del período actual.
            </p>
            {subscription?.subscriptionCurrentPeriodEnd && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-yellow-800">
                  <strong>Nota:</strong> Mantendrás acceso hasta el{" "}
                  <strong>
                    {formatDate(subscription.subscriptionCurrentPeriodEnd)}
                  </strong>
                  .
                </p>
              </div>
            )}
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setShowCancelModal(false)}
                disabled={isCancelling}
              >
                Volver
              </Button>
              <Button
                variant="danger"
                className="flex-1"
                onClick={handleCancelSubscription}
                disabled={isCancelling}
              >
                {isCancelling ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Cancelando...
                  </>
                ) : (
                  "Sí, cancelar"
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de cancelación de add-on */}
      {addOnToCancel && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-full">
                <XCircle className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900">
                ¿Cancelar {addOnToCancel.name}?
              </h3>
            </div>
            <p className="text-gray-600 mb-4">
              El servicio seguirá activo hasta el final del período que ya
              tienes pagado y no se te volverá a cobrar. Podrás reactivarlo en
              cualquier momento antes de esa fecha sin perder nada.
            </p>
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setAddOnToCancel(null)}
                disabled={cancellingAddOn === addOnToCancel.id}
              >
                No, mantener
              </Button>
              <Button
                variant="danger"
                className="flex-1"
                onClick={() => handleCancelAddOn(addOnToCancel.id)}
                disabled={cancellingAddOn === addOnToCancel.id}
              >
                {cancellingAddOn === addOnToCancel.id ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Cancelando...
                  </>
                ) : (
                  "Sí, cancelar servicio"
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
